import { message } from "antd";
import React from "react";
import {
  CompanyRadarRole,
  useGetCompanyBadgesQuery,
  useGetCompanyProfileQuery,
  useGetCompanyRadarMetricsQuery,
  useGetMyBadgesQuery,
  useUpdateMyCompanyLogoMutation,
} from "reducers/companyProfileApi";
import { useGetCompanyXpQuery } from "reducers/expApi";
import CompanyBadges from "./CompanyBadges";
import CompanyInfo from "./CompanyInfo";
import CompanyMetrics from "./CompanyMetrics";
import StructureScore from "./StructureScore";

interface PublicCompanyProfileProps {
  companyCode?: string;
  canEditAvatar?: boolean;
  role: CompanyRadarRole;
}

const PublicCompanyProfile = ({
  companyCode,
  canEditAvatar = false,
  role,
}: PublicCompanyProfileProps) => {
  const companyQuery = useGetCompanyProfileQuery(
    { companyCode: companyCode ?? "" },
    { skip: !companyCode },
  );
  const metricsQuery = useGetCompanyRadarMetricsQuery(
    { companyCode: companyCode ?? "" },
    { skip: !companyCode },
  );
  const xpQuery = useGetCompanyXpQuery(
    { companyCode: companyCode ?? "" },
    { skip: !companyCode },
  );
  const badgesQuery = useGetMyBadgesQuery(undefined, {
    skip: !canEditAvatar,
  });
  const companyBadgesQuery = useGetCompanyBadgesQuery(
    { companyId: companyQuery.data?.data.id.toString() ?? "" },
    { skip: canEditAvatar || !companyQuery.data?.data.id },
  );
  const [updateMyCompanyLogo, updateLogoState] =
    useUpdateMyCompanyLogoMutation();

  const badges = canEditAvatar
    ? (badgesQuery.data ?? [])
    : (companyBadgesQuery.data ?? []);
  const isBadgesLoading = canEditAvatar
    ? badgesQuery.isLoading
    : companyQuery.isLoading || companyBadgesQuery.isLoading;
  const isBadgesError = canEditAvatar
    ? badgesQuery.isError
    : companyQuery.isError || companyBadgesQuery.isError;

  const handleAvatarUpload = async (file: File) => {
    if (!canEditAvatar || !companyCode) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      await updateMyCompanyLogo({ companyCode, data: formData }).unwrap();
      message.success("プロフィール画像を更新しました");
    } catch (error) {
      message.error("プロフィール画像の更新に失敗しました");
      throw error;
    }
  };

  return (
    <div className="flex w-full flex-col gap-[30px]">
      <CompanyInfo
        companyName={companyQuery.data?.data.company_name}
        logoUrl={companyQuery.data?.data.logo_url}
        canEditAvatar={canEditAvatar}
        isAvatarBusy={updateLogoState.isLoading}
        onAvatarUpload={handleAvatarUpload}
        isLoading={companyQuery.isLoading || !companyCode}
        isError={companyQuery.isError}
        level={xpQuery.data?.data.current_level}
      />
      <div className="grid grid-cols-1 gap-[20px] xl:grid-cols-[400px_minmax(0,1fr)]">
        <StructureScore companyCode={companyCode} />
        <CompanyMetrics
          role={role}
          metrics={metricsQuery.data?.data.metrics}
          isLoading={metricsQuery.isLoading || !companyCode}
          isError={metricsQuery.isError}
        />
      </div>
      <CompanyBadges
        badges={badges}
        isLoading={isBadgesLoading}
        isError={isBadgesError}
      />
    </div>
  );
};

export default PublicCompanyProfile;
